import { Injectable } from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import {
  AuthenticationResult,
  EventMessage,
  EventType,
  InteractionType,
  PopupRequest,
  RedirectRequest
} from '@azure/msal-browser';
import { Observable, BehaviorSubject, filter, of, switchMap, from } from 'rxjs';
import { environment } from '../../environments/environment';

/**
 * Authentication service handling Azure AD login/logout via MSAL
 */
@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private isAuthenticatedSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isAuthenticated$: Observable<boolean> = this.isAuthenticatedSubject.asObservable();

  private userRolesSubject: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
  public userRoles$: Observable<string[]> = this.userRolesSubject.asObservable();

  constructor(
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService
  ) {
    this.initializeAuth();
  }

  /**
   * Initialize MSAL authentication and listen for events
   */
  private initializeAuth(): void {
    // Check if user is already logged in
    const accounts = this.msalService.instance.getAllAccounts();
    if (accounts.length > 0) {
      this.isAuthenticatedSubject.next(true);
      this.extractUserRoles(accounts[0]);
    }

    // Listen for MSAL events
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS || msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS)
      )
      .subscribe((result: EventMessage) => {
        const payload = result.payload as AuthenticationResult;
        this.msalService.instance.setActiveAccount(payload.account);
        this.isAuthenticatedSubject.next(true);
        this.extractUserRoles(payload.account);
      });

    // Handle logout
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGOUT_SUCCESS)
      )
      .subscribe(() => {
        this.isAuthenticatedSubject.next(false);
        this.userRolesSubject.next([]);
      });
  }

  /**
   * Login using redirect flow
   */
  login(redirectRequest?: RedirectRequest): Observable<void> {
    const request: RedirectRequest = {
      ...redirectRequest,
      scopes: [environment.azureAd.apiScope],
      redirectUri: environment.azureAd.redirectUri
    };

    return from(this.msalService.loginRedirect(request));
  }

  /**
   * Login using popup flow (alternative)
   */
  loginPopup(popupRequest?: PopupRequest): Observable<AuthenticationResult> {
    const request: PopupRequest = {
      ...popupRequest,
      scopes: [environment.azureAd.apiScope]
    };

    return from(this.msalService.loginPopup(request));
  }

  /**
   * Logout current user
   */
  logout(): void {
    this.msalService.logoutRedirect().subscribe();
  }

  /**
   * Get current active account
   */
  get activeAccount() {
    return this.msalService.instance.getActiveAccount();
  }

  /**
   * Get all accounts
   */
  get allAccounts() {
    return this.msalService.instance.getAllAccounts();
  }

  /**
   * Get authentication token for API calls
   */
  getAccessToken(): Observable<string> {
    const account = this.msalService.instance.getActiveAccount();
    if (!account) {
      return of('');
    }

    return from(
      this.msalService.acquireTokenSilent({
        scopes: [environment.azureAd.apiScope],
        account: account
      })
    ).pipe(
      switchMap((result: AuthenticationResult) => of(result.accessToken))
    );
  }

  /**
   * Extract user roles from ID token claims
   */
  private extractUserRoles(account: any): void {
    const idTokenClaims = account.idTokenClaims as any;
    const roles = idTokenClaims?.roles || [];
    this.userRolesSubject.next(roles);
  }

  hasRole(role: string): boolean {
    return this.userRolesSubject.value.includes(role);
  }

  /**
   * Check if user has any of the specified roles
   */
  hasAnyRole(roles: string[]): boolean {
    return roles.some(role => this.hasRole(role));
  }

  /**
   * Get user display name
   */
  getUserName(): string {
    const account = this.activeAccount;
    return account?.name || account?.username || 'Unknown User';
  }

  /**
   * Get user email
   */
  getUserEmail(): string {
    const account = this.activeAccount;
    return account?.username || '';
  }
}
